import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useParams } from 'react-router-dom';
import { MENUITEM_IDS } from '@/lib';
import { StanButton } from '@/components';
import { Skeleton } from '@/components/core-components/Skeleton/Skeleton';
import { useGlobalStore } from '@/stores';
import { useStanRouter } from '@/hooks/useStanRouter';
import {
    EditFunnelDetailsModal,
    FunnelPageEditor,
    FunnelPagesNavigation,
    UpgradeFunnelThemeModal,
} from '@/features/funnels/components';
import { ShareFunnelModal } from '@/features/funnels/components/editor/modals/ShareFunnelModal';
import { HideFunnelEntryPageModal } from '@/features/funnels/components/editor/modals/HideFunnelEntryPageModal';
import { FunnelJourneyPreview } from '@/features/funnels/components/editor/preview/FunnelJourneyPreview';
import { useFunnelJourneyPreview } from '@/features/funnels/components/editor/preview/useFunnelJourneyPreview';
import { FunnelSummaryCard } from '@/features/funnels/components/editor/navigation/FunnelSummaryCard';
import { useFunnelEditorData } from '@/features/funnels/hooks/useFunnelEditorData';
import { useFunnelEditorSelection } from '@/features/funnels/hooks/useFunnelEditorSelection';
import { useFunnelAutoRename } from '@/features/funnels/hooks/useFunnelAutoRename';
import { useFunnelPublishPage } from '@/features/funnels/hooks/useFunnelPublishPage';
import { useFunnelReorderPages } from '@/features/funnels/hooks/useFunnelReorderPages';
import { useFunnelDetachPage } from '@/features/funnels/hooks/useFunnelDetachPage';
import { useFunnelPageActions } from '@/features/funnels/hooks/useFunnelPageActions';
import { useFunnelHidePage } from '@/features/funnels/hooks/useFunnelHidePage';
import { useAddFunnelToStore } from '@/features/funnels/hooks/useAddFunnelToStore';
import {
    buildFunnelPageTree,
    getFunnelDisplayName,
    getFunnelPageRole,
    isAtMaxFunnelPages,
} from '@/features/funnels/logic';
import { PageStatus } from '@/types/store';
import { DRAFT_FUNNEL_PAGE_ID, MAX_FUNNEL_PAGES } from '@/features/funnels/constants/funnels.constants';
import { getFunnelTemplate, readFunnelTemplate } from '@/features/funnels/constants/funnelTemplates';
import type { EditFunnelDetailsState } from '@/features/funnels/types';
import {
    FunnelJourneyNavPanel,
    FunnelJourneyRevenueCard,
} from '@/features/funnels/components/editor/preview/FunnelJourneyNavPanel';

const FunnelDetailPage = () => {
    const { storeId: storeIdParam } = useParams<{ storeId: string }>();
    const storeId = storeIdParam ? Number(storeIdParam) : undefined;
    const { push } = useStanRouter();
    const setBreadcrumbs = useGlobalStore(state => state.setBreadcrumbs);

    const { funnel, isLoading, savedFunnelPages, funnelPages, productInfoByPageId, maxSavedOrder } =
        useFunnelEditorData(storeId);

    const {
        selectedPageId,
        selectedPage,
        selectedStepNumber,
        selectedSuggestedIndex,
        isDraftSelected,
        isLegacySelected,
        hasDraftPage,
        isSelectedStepUnlocked,
        isAddPageUnlocked,
        handleSelectPage,
        handleSelectSuggested,
        handleAddPage,
    } = useFunnelEditorSelection({ funnelPages, savedFunnelPages, maxSavedOrder });

    const maybeAutoRenameFunnel = useFunnelAutoRename(storeId, funnel);
    const { handlePublishPage, isPublishing } = useFunnelPublishPage({
        storeId,
        savedFunnelPages,
        maybeAutoRenameFunnel,
    });
    const handleReorderPages = useFunnelReorderPages({ storeId, savedFunnelPages });
    const { handleDetachFromParent, isDetaching } = useFunnelDetachPage({ storeId, savedFunnelPages });
    const { handleDeletePage, handleDuplicatePage, isDeleting } = useFunnelPageActions(storeId);
    const {
        requestHide: handleHidePage,
        pendingEntryHide,
        confirmEntryHide,
        cancelEntryHide,
        isHiding,
    } = useFunnelHidePage({ storeId, savedFunnelPages });

    const [editDetailsState, setEditDetailsState] = useState<EditFunnelDetailsState | null>(null);
    const [showUpgradeThemeModal, setShowUpgradeThemeModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [showJourneyPreview, setShowJourneyPreview] = useState(false);
    const editorRef = useRef<HTMLDivElement | null>(null);

    const journeyState = useFunnelJourneyPreview(savedFunnelPages, productInfoByPageId);
    const journeyNavSlots = useMemo(
        () => buildFunnelPageTree(journeyState.publishedPages),
        [journeyState.publishedPages]
    );

    const funnelName = funnel ? getFunnelDisplayName(funnel) : 'Funnel';
    const showUpgradeFunnelStyle = funnel?.type === 'funnel' && funnel.data?.legacy_funnel === true;
    const templateMinRows = getFunnelTemplate(readFunnelTemplate(storeId)).minRows;

    useEffect(() => {
        setBreadcrumbs([
            { title: 'My Funnels', to: '/funnels', id: MENUITEM_IDS.FUNNELS },
            { title: funnelName, to: '', id: 'funnel-detail', active: true },
        ]);
    }, [setBreadcrumbs, funnelName]);

    // Reset journey state when entering preview
    useEffect(() => {
        if (showJourneyPreview) {
            journeyState.onRestart();
        }
    }, [showJourneyPreview, journeyState.onRestart]);

    const handleEditDetails = useCallback(() => {
        if (!funnel) return;
        setEditDetailsState({
            storeId: funnel.store_id,
            name: funnelName,
            slug: funnel.slug,
        });
    }, [funnel, funnelName]);

    const scrollToEditorOnStackedLayout = useCallback(() => {
        if (window.matchMedia('(min-width: 1280px)').matches) return;
        window.requestAnimationFrame(() => {
            editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    }, []);

    const handleSelectPageAndScroll = useCallback(
        (pageId: number) => {
            handleSelectPage(pageId);
            scrollToEditorOnStackedLayout();
        },
        [handleSelectPage, scrollToEditorOnStackedLayout]
    );

    const handleSelectSuggestedAndScroll = useCallback(
        (index: number) => {
            handleSelectSuggested(index);
            scrollToEditorOnStackedLayout();
        },
        [handleSelectSuggested, scrollToEditorOnStackedLayout]
    );

    const handleAddPageAndScroll = useCallback(() => {
        handleAddPage();
        scrollToEditorOnStackedLayout();
    }, [handleAddPage, scrollToEditorOnStackedLayout]);

    if (isLoading) {
        return <FunnelDetailSkeleton />;
    }

    if (!funnel) {
        return <FunnelNotFound onBack={() => push('/funnels')} />;
    }

    return (
        <>
            <div className='mx-auto flex w-full max-w-screen-2xl flex-col gap-6 pb-24 lg:pb-36'>
                <FunnelDetailHeader
                    name={funnelName}
                    publishedPageCount={savedFunnelPages.filter(p => p.status === PageStatus.PUBLISHED).length}
                    onEditDetails={handleEditDetails}
                    onShare={() => setShowShareModal(true)}
                />

                {showUpgradeFunnelStyle && (
                    <LegacyFunnelStyleNotice onUpgradeClick={() => setShowUpgradeThemeModal(true)} />
                )}

                <div className='flex flex-col gap-8 xl:flex-row'>
                    {/* Left column: nav or journey nav */}
                    <div className='flex w-full flex-col gap-3 md:shrink-0 xl:max-w-md'>
                        {showJourneyPreview ? (
                            <>
                                <FunnelJourneyRevenueCard
                                    earnedCents={journeyState.earnedCents}
                                    currency={journeyState.currency}
                                    revenueJustIncreased={journeyState.revenueJustIncreased}
                                />
                                <FunnelJourneyNavPanel
                                    navSlots={journeyNavSlots}
                                    currentPageId={journeyState.currentPage?.page_id ?? null}
                                    visitedIds={journeyState.visitedIds}
                                    history={journeyState.history}
                                    productInfoByPageId={productInfoByPageId}
                                    positionByPageId={journeyState.positionByPageId}
                                    relationshipByPageId={journeyState.relationshipByPageId}
                                    isComplete={journeyState.isComplete}
                                    onExit={() => setShowJourneyPreview(false)}
                                />
                            </>
                        ) : (
                            <>
                                <FunnelSummaryCard pages={savedFunnelPages} />
                                <FunnelPagesNavigation
                                    pages={funnelPages}
                                    selectedPageId={selectedPageId}
                                    selectedSuggestedIndex={selectedSuggestedIndex}
                                    onSelectPage={handleSelectPageAndScroll}
                                    onSelectSuggested={handleSelectSuggestedAndScroll}
                                    onAddPage={handleAddPageAndScroll}
                                    onReorderPages={handleReorderPages}
                                    onHidePage={handleHidePage}
                                    onDuplicatePage={handleDuplicatePage}
                                    onDeletePage={handleDeletePage}
                                    onPreview={() => setShowJourneyPreview(true)}
                                    productInfoByPageId={productInfoByPageId}
                                    minRows={templateMinRows}
                                    isAddDisabled={
                                        hasDraftPage || !isAddPageUnlocked || isAtMaxFunnelPages(savedFunnelPages)
                                    }
                                    addDisabledReason={
                                        isAtMaxFunnelPages(savedFunnelPages)
                                            ? `You've hit the ${MAX_FUNNEL_PAGES}-page limit for a single funnel.`
                                            : undefined
                                    }
                                    isDeleting={isDeleting}
                                />
                                <AddFunnelToStoreLink
                                    storeId={funnel.store_id}
                                    slug={funnel.slug}
                                    funnelName={funnelName}
                                />
                            </>
                        )}
                    </div>

                    {/* Right column: editor or journey preview */}
                    <div ref={editorRef} className='min-w-0 flex-1 scroll-mt-4'>
                        {showJourneyPreview ? (
                            <FunnelJourneyPreview
                                state={journeyState}
                                productInfoByPageId={productInfoByPageId}
                                onExit={() => setShowJourneyPreview(false)}
                            />
                        ) : selectedPage ? (
                            <FunnelPageEditor
                                key={
                                    selectedPage.page_id === DRAFT_FUNNEL_PAGE_ID
                                        ? `draft-${selectedStepNumber}`
                                        : selectedPage.page_id
                                }
                                page={selectedPage}
                                stepNumber={selectedStepNumber}
                                storeId={funnel.store_id}
                                isDraft={isDraftSelected}
                                isLegacy={isLegacySelected}
                                isPublishing={isPublishing}
                                isDeleting={isDeleting}
                                isHiding={isHiding}
                                isDetaching={isDetaching}
                                isStepUnlocked={isSelectedStepUnlocked}
                                allPages={funnelPages}
                                productInfoByPageId={productInfoByPageId}
                                onPublish={handlePublishPage}
                                onDelete={handleDeletePage}
                                onHide={handleHidePage}
                                onDetachFromParent={handleDetachFromParent}
                            />
                        ) : (
                            <EmptyFunnelEditor
                                nextPageRole={getFunnelPageRole(
                                    funnelPages.filter(p => p.page_id !== DRAFT_FUNNEL_PAGE_ID).length
                                )}
                            />
                        )}
                    </div>
                </div>
            </div>

            <EditFunnelDetailsModal
                isOpen={!!editDetailsState}
                onClose={() => setEditDetailsState(null)}
                storeId={editDetailsState?.storeId ?? 0}
                currentName={editDetailsState?.name ?? ''}
                currentSlug={editDetailsState?.slug ?? ''}
            />

            <UpgradeFunnelThemeModal
                isOpen={showUpgradeThemeModal}
                onClose={() => setShowUpgradeThemeModal(false)}
                storeId={funnel.store_id}
            />

            <ShareFunnelModal
                isOpen={showShareModal}
                onClose={() => setShowShareModal(false)}
                storeId={funnel.store_id}
                slug={funnel.slug ?? ''}
                funnelName={funnelName}
                publishedPageCount={savedFunnelPages.filter(p => p.status === PageStatus.PUBLISHED).length}
            />

            <HideFunnelEntryPageModal
                isOpen={!!pendingEntryHide}
                onClose={cancelEntryHide}
                pageTitle={pendingEntryHide?.pageToHide.title ?? ''}
                newEntryTitle={pendingEntryHide?.newEntryPage.title ?? ''}
                isHiding={isHiding}
                onConfirm={confirmEntryHide}
            />
        </>
    );
};

const FunnelDetailHeader = ({
    name,
    publishedPageCount,
    onEditDetails,
    onShare,
}: {
    name: string;
    publishedPageCount: number;
    onEditDetails: () => void;
    onShare: () => void;
}) => {
    const shareIsReady = publishedPageCount > 1;
    return (
        <div className='flex flex-col items-start gap-3 sm:flex-row sm:items-start sm:justify-between'>
            <h2 className='text-dark w-full min-w-0 break-words sm:flex-1'>{name}</h2>
            <div className='flex shrink-0 items-center gap-2'>
                <StanButton name='edit-funnel-details-button' variant='outline' size='sm' onClick={onEditDetails}>
                    Edit Details
                </StanButton>
                <StanButton
                    name='share-funnel-button'
                    variant={shareIsReady ? undefined : 'outline'}
                    size='sm'
                    onClick={onShare}
                >
                    Share
                </StanButton>
            </div>
        </div>
    );
};

const AddFunnelToStoreLink = ({ storeId, slug, funnelName }: { storeId: number; slug: string; funnelName: string }) => {
    const addToStore = useAddFunnelToStore();
    return (
        <button
            type='button'
            onClick={() => addToStore({ storeId, slug, name: funnelName })}
            className='text-brand-main hover:text-brand-dark text-body-xs cursor-pointer self-start font-semibold underline-offset-2 hover:underline'
        >
            + Add to My Store
        </button>
    );
};

const LegacyFunnelStyleNotice = ({ onUpgradeClick }: { onUpgradeClick: () => void }) => (
    <p className='text-body-sm text-gray'>
        This funnel is currently using an old design.{' '}
        <button type='button' className='text-brand-main cursor-pointer font-bold underline' onClick={onUpgradeClick}>
            Upgrade to new design
        </button>
    </p>
);

const EmptyFunnelEditor = ({ nextPageRole }: { nextPageRole: string }) => (
    <div className='bg-canvas shadow-stan flex flex-1 flex-col items-center justify-center gap-3 rounded-2xl p-12'>
        <p className='text-body-md text-gray text-center'>
            Add your <span className='text-dark font-semibold'>{nextPageRole}</span> to move on.
        </p>
    </div>
);

const FunnelDetailSkeleton = () => (
    <div className='flex w-full flex-col gap-6'>
        <div className='flex items-center justify-between gap-4'>
            <Skeleton className='h-8 w-56' />
            <Skeleton className='h-9 w-28' />
        </div>
        <div className='flex flex-col gap-8 md:flex-row'>
            <Skeleton className='h-96 w-full rounded-2xl md:w-96' />
            <Skeleton className='h-96 flex-1 rounded-2xl' />
        </div>
    </div>
);

const FunnelNotFound = ({ onBack }: { onBack: () => void }) => (
    <div className='flex w-full flex-col items-center justify-center gap-4 py-12'>
        <p className='text-body-md text-gray'>Funnel not found</p>
        <StanButton name='back-to-funnels' variant='outline' onClick={onBack}>
            Back to Funnels
        </StanButton>
    </div>
);

export default FunnelDetailPage;
